import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from '../../../theme';
import { ActivityIndicator, Modal, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import styles from "../MyTicketsScreenStyles";
import mobileStyles, { STATUS_BORDER_COLOR } from "../MyTicketsScreen.mobile.style";
import { MyTicketRow } from "../../../store/slices/ticketingPortalSlice";
import { STATUS_BG_DARK, STATUS_COLOR_DARK } from "../../../constants/ticketStatusConstants";
import {
  SortColumn,
  SortDirection,
  WebFilters,
  FilterOptions,
} from "../hooks/useMyTicketsListing";

type Props = {
  query: string;
  totalCount: number;
  filteredTickets: MyTicketRow[];
  onQueryChange: (value: string) => void;
  onRowPress?: (ticketId: string) => void;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onSort: (col: SortColumn) => void;
  webFilters: WebFilters;
  onWebFilterChange: (filters: WebFilters) => void;
  filterOptions: FilterOptions;
  mobile?: boolean;
  filterCount?: number;
  onFiltersPress?: () => void;
  /** Navigate to Submit Ticket — shown in the "no tickets yet" empty state. */
  onNewTicket?: () => void;
  /** Called by "Clear Filters" in the "no filter results" empty state — should reset both filters and search query. */
  onClearFilters?: () => void;
  /** When set, the service-unavailable error state is shown instead of the table. */
  error?: string | null;
  /** While true, the table body is suppressed (loading skeleton or just nothing). */
  loading?: boolean;
  /** Current page number (1-based) for server-side pagination. */
  currentPage?: number;
  /** Total number of pages based on server totalElements. */
  totalPages?: number;
  /** Called when the user clicks a pagination control. */
  onPageChange?: (page: number) => void;
  /**
   * When set, the table scrolls to the row with this ticket ID after rendering.
   * Used to restore position when returning from Ticket Detail.
   */
  scrollToTicketId?: string | null;
};

/* ── Helpers ─────────────────────────────────────────────── */
function formatUrgencyLabel(urgency?: string | null): string {
  const normalized = String(urgency ?? "").trim();
  if (!normalized) return "—";
  return URGENCY_LABEL[normalized as keyof typeof URGENCY_LABEL] ?? normalized;
}

function getMobileUrgencyBadgeStyles(urgency?: string | null) {
  switch (urgency) {
    case "critical":
      return {
        badgeStyle: mobileStyles.urgencyCritical,
        dotStyle: mobileStyles.urgencyCriticalDot,
        textStyle: mobileStyles.urgencyCriticalText,
      };
    case "high":
      return {
        badgeStyle: mobileStyles.urgencyHigh,
        dotStyle: mobileStyles.urgencyHighDot,
        textStyle: mobileStyles.urgencyHighText,
      };
    case "medium":
      return {
        badgeStyle: mobileStyles.urgencyMedium,
        dotStyle: mobileStyles.urgencyMediumDot,
        textStyle: mobileStyles.urgencyMediumText,
      };
    case "low":
      return {
        badgeStyle: mobileStyles.urgencyLow,
        dotStyle: mobileStyles.urgencyLowDot,
        textStyle: mobileStyles.urgencyLowText,
      };
    case "informational":
      return {
        badgeStyle: mobileStyles.urgencyInformational,
        dotStyle: mobileStyles.urgencyInformationalDot,
        textStyle: mobileStyles.urgencyInformationalText,
      };
    default:
      return {
        badgeStyle: { backgroundColor: "#F3F4F6" },
        dotStyle: { backgroundColor: "#CBD5E1" },
        textStyle: { color: "#6B7280" },
      };
  }
}

function getDesktopUrgencyBadgeStyles(urgency?: string | null) {
  switch (urgency) {
    case "critical":
      return {
        badgeStyle: styles.urgencyCritical,
        dotStyle: styles.urgencyCriticalDot,
        textStyle: styles.urgencyCriticalText,
      };
    case "high":
      return {
        badgeStyle: styles.urgencyHigh,
        dotStyle: styles.urgencyHighDot,
        textStyle: styles.urgencyHighText,
      };
    case "medium":
      return {
        badgeStyle: styles.urgencyMedium,
        dotStyle: styles.urgencyMediumDot,
        textStyle: styles.urgencyMediumText,
      };
    case "low":
      return {
        badgeStyle: styles.urgencyLow,
        dotStyle: styles.urgencyLowDot,
        textStyle: styles.urgencyLowText,
      };
    case "informational":
      return {
        badgeStyle: styles.urgencyInformational,
        dotStyle: styles.urgencyInformationalDot,
        textStyle: styles.urgencyInformationalText,
      };
    default:
      return {
        badgeStyle: { backgroundColor: "#F3F4F6" },
        dotStyle: { backgroundColor: "#CBD5E1" },
        textStyle: { color: "#6B7280" },
      };
  }
}

/** Format ISO datetime (UTC) to device local timezone — e.g. "05/18/2026 06:33 AM" */
function formatDateLocal(iso: string): string {
  if (!iso) return "—";
  // The API sends UTC timestamps without a 'Z' suffix (e.g. "2026-05-18T11:33:07.230423").
  // Without a timezone indicator JavaScript may parse the string as local time instead of UTC.
  // Append 'Z' to force UTC interpretation so the local-timezone getters return the correct time.
  const utcIso = /Z$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + "Z";
  const d = new Date(utcIso);
  if (isNaN(d.getTime())) return "—";

  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const year = d.getFullYear();

  let hour = d.getHours();
  const minute = String(d.getMinutes()).padStart(2, "0");
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  const hourStr = String(hour).padStart(2, "0");

  // Derive short timezone abbreviation (e.g. PDT, EST) so the user sees which
  // time zone the displayed time is in — required by the user story.
  let tz = "";
  try {
    tz = " " + (Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
      .formatToParts(d)
      .find(p => p.type === "timeZoneName")?.value ?? "");
  } catch { /* Intl unavailable on some RN engines — omit gracefully */ }

  return `${month}/${day}/${year} ${hourStr}:${minute} ${ampm}${tz}`;
}

function truncate(str: string, max: number): string {
  return str.length > max ? str.slice(0, max) + "…" : str;
}

function formatAccessRequestLocation(ticket: MyTicketRow): string {
  const locations = ticket.accessRequest?.locations;
  if (!Array.isArray(locations) || locations.length === 0) {
    return ticket.location;
  }

  const first = locations[0];
  const primary = first.locationName || first.market || first.region || first.locationId || ticket.location;
  const extraCount = locations.length - 1;
  return extraCount > 0 ? `${primary} +${extraCount}` : primary;
}

const URGENCY_LABEL: Record<string, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
  informational: "Informational",
};

const DECLINED_PILL_STYLE = {
  alignSelf: "flex-start" as const,
  backgroundColor: "#FEE4E2",
  borderRadius: 100,
  paddingHorizontal: 8,
  paddingVertical: 2,
  marginTop: 4,
};
const DECLINED_PILL_TEXT_STYLE = { fontSize: 10, fontWeight: "700" as const, color: "#B42318" };

const STATUS_LABEL: Record<string, string> = {
  new: "New",
  progress: "In Progress",
  "waiting-it": "Escalated to IT",
  "waiting-user": "Waiting on User",
  "waiting-vendor": "Waiting on Vendor",
  "waiting-tmv": "Waiting on Vendor",
  "waiting-vendor": "Waiting on Vendor",
  "submitted-ado": "Submitted to ADO",
  resolved: "Closed",
  "closed-merged": "Closed (Merged)",
};

/* ── Sort header ─────────────────────────────────────────── */
type SortHeaderProps = {
  label: string;
  col: SortColumn;
  current: SortColumn;
  direction: SortDirection;
  onSort: (col: SortColumn) => void;
  style?: object;
};

const SortHeader: React.FC<SortHeaderProps> = ({ label, col, current, direction, onSort, style }) => {
  const isActive = current === col;
  return (
    <Pressable
      style={[styles.thCell, styles.thSortable, style]}
      onPress={() => onSort(col)}
    >
      <Text style={[styles.thCellText, isActive && styles.thCellTextActive]}>{label}</Text>
      <View style={styles.sortArrows}>
        <MaterialIcons
          name="arrow-drop-up"
          size={14}
          color={isActive && direction === "asc" ? "#034895" : "#c0c8d4"}
        />
        <MaterialIcons
          name="arrow-drop-down"
          size={14}
          color={isActive && direction === "desc" ? "#034895" : "#c0c8d4"}
          style={{ marginTop: -6 }}
        />
      </View>
    </Pressable>
  );
};

/* ── Sort + inline column filter header ───────────────────── */
type SortFilterHeaderProps = {
  label: string;
  col: SortColumn;
  current: SortColumn;
  direction: SortDirection;
  onSort: (col: SortColumn) => void;
  filterKey?: keyof WebFilters;
  filterOptions?: string[];
  filterLabelMap?: Record<string, string>;
  selected?: string[];
  onToggle?: (key: keyof WebFilters, value: string) => void;
  onClear?: (key: keyof WebFilters) => void;
  style?: object;
};

const SortFilterHeader: React.FC<SortFilterHeaderProps> = ({
  label, col, current, direction, onSort,
  filterKey, filterOptions = [], filterLabelMap, selected = [],
  onToggle, onClear, style,
}) => {
  const isSortActive = current === col;
  const isFiltered = selected.length > 0;
  const canFilter = !!filterKey && filterOptions.length > 0;
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const filterBtnRef = useRef<View>(null);

  function handleFilterPress() {
    const el = filterBtnRef.current as any;
    if (Platform.OS === "web" && el && el.getBoundingClientRect) {
      const rect = el.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 200) });
    } else if (el && el.measure) {
      // React Native measure for native iOS (e.g. iPad)
      el.measure((_fx: number, _fy: number, w: number, _h: number, px: number, py: number) => {
        setMenuPos({ top: py + _h + 4, left: px, width: Math.max(w, 200) });
      });
    }
    setMenuOpen(true);
  }

  // Web: use a ReactDOM portal so the dropdown overlays the scrollable table
  const webMenuPortal = (() => {
    if (!menuOpen || !canFilter || Platform.OS !== "web") return null;
    // Dynamically access ReactDOM to avoid bundling it in native builds
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const ReactDOM = require("react-dom");
      const docBody = (globalThis as any).document?.body;
      if (!docBody) return null;
      return ReactDOM.createPortal(
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => setMenuOpen(false)} />
          <div
            style={{
              position: "fixed",
              top: menuPos.top,
              left: menuPos.left,
              minWidth: menuPos.width,
              zIndex: 9999,
              background: "#fff",
              border: "1px solid #d7e5f8",
              borderRadius: 8,
              boxShadow: "0 4px 16px rgba(0,94,192,0.12)",
              padding: "6px 0",
              maxHeight: 280,
              overflowY: "auto",
            }}
          >
            {filterOptions.map((opt) => {
              const checked = selected.includes(opt);
              const display = filterLabelMap?.[opt] ?? opt;
              return (
                <div
                  key={opt}
                  onClick={(e) => { e.stopPropagation(); onToggle?.(filterKey!, opt); }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "7px 14px",
                    cursor: "pointer",
                    background: checked ? "#EEF5FF" : "transparent",
                    fontSize: 13,
                    color: checked ? "#034895" : "#2d2d2d",
                    fontWeight: checked ? 600 : 400,
                  }}
                >
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      border: `2px solid ${checked ? "#034895" : "#b0b8c4"}`,
                      background: checked ? "#034895" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {checked && <span style={{ color: "#fff", fontSize: 11, lineHeight: 1 }}>✓</span>}
                  </span>
                  {display}
                </div>
              );
            })}
            {selected.length > 0 && (
              <div
                onClick={(e) => { e.stopPropagation(); onClear?.(filterKey!); setMenuOpen(false); }}
                style={{
                  borderTop: "1px solid #ebebeb",
                  padding: "7px 14px",
                  cursor: "pointer",
                  fontSize: 12,
                  color: "#CC3B0F",
                  fontWeight: 600,
                }}
              >
                Clear
              </div>
            )}
          </div>
        </>,
        docBody
      );
    } catch {
      return null;
    }
  })();

  // Native iOS (iPad): use a React Native Modal instead of a DOM portal
  const nativeMenuModal = menuOpen && canFilter && Platform.OS !== "web" ? (
    <Modal
      transparent
      visible={menuOpen}
      animationType="none"
      onRequestClose={() => setMenuOpen(false)}
    >
      <Pressable style={sfStyles.modalBackdrop} onPress={() => setMenuOpen(false)} />
      <View
        style={[
          sfStyles.nativeMenu,
          { top: menuPos.top, left: menuPos.left, minWidth: Math.max(menuPos.width, 200) },
        ]}
      >
        <ScrollView style={{ maxHeight: 260 }} keyboardShouldPersistTaps="handled">
          {filterOptions.map((opt) => {
            const checked = selected.includes(opt);
            const display = filterLabelMap?.[opt] ?? opt;
            return (
              <Pressable
                key={opt}
                onPress={() => { onToggle?.(filterKey!, opt); }}
                style={[sfStyles.menuItem, checked && sfStyles.menuItemChecked]}
              >
                <View style={[sfStyles.checkbox, checked && sfStyles.checkboxChecked]}>
                  {checked && <Text style={sfStyles.checkmark}>✓</Text>}
                </View>
                <Text style={[sfStyles.menuItemText, checked && sfStyles.menuItemTextChecked]}>
                  {display}
                </Text>
              </Pressable>
            );
          })}
          {selected.length > 0 && (
            <Pressable
              onPress={() => { onClear?.(filterKey!); setMenuOpen(false); }}
              style={sfStyles.clearItem}
            >
              <Text style={sfStyles.clearItemText}>Clear</Text>
            </Pressable>
          )}
        </ScrollView>
      </View>
    </Modal>
  ) : null;

  return (
    <View style={[styles.thCell, styles.thSortFilterWrap, style]}>
      <Pressable style={styles.thSortArea} onPress={() => onSort(col)}>
        <Text style={[styles.thCellText, isSortActive && styles.thCellTextActive]}>{label}</Text>
        <View style={styles.sortArrows}>
          <MaterialIcons
            name="arrow-drop-up"
            size={14}
            color={isSortActive && direction === "asc" ? "#034895" : "#c0c8d4"}
          />
          <MaterialIcons
            name="arrow-drop-down"
            size={14}
            color={isSortActive && direction === "desc" ? "#034895" : "#c0c8d4"}
            style={{ marginTop: -6 }}
          />
        </View>
      </Pressable>
      {canFilter && (
        <Pressable
          ref={filterBtnRef}
          onPress={handleFilterPress}
          style={[styles.thFilterIcon, isFiltered && styles.thFilterIconActive]}
        >
          <MaterialIcons name="filter-list" size={13} color={isFiltered ? "#034895" : "#c0c8d4"} />
        </Pressable>
      )}
      {webMenuPortal}
      {nativeMenuModal}
    </View>
  );
};

/* ── SortFilterHeader native-iOS dropdown styles ──────────── */
const sfStyles = StyleSheet.create({
  modalBackdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  nativeMenu: {
    position: "absolute",
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#d7e5f8",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 8,
    paddingVertical: 6,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  menuItemChecked: {
    backgroundColor: "#EEF5FF",
  },
  checkbox: {
    width: 16,
    height: 16,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: "#b0b8c4",
    alignItems: "center",
    justifyContent: "center",
  },
  checkboxChecked: {
    borderColor: "#034895",
    backgroundColor: "#034895",
  },
  checkmark: {
    color: "#fff",
    fontSize: 10,
    lineHeight: 12,
  },
  menuItemText: {
    fontSize: 13,
    color: "#2d2d2d",
  },
  menuItemTextChecked: {
    color: "#034895",
    fontWeight: "600",
  },
  clearItem: {
    borderTopWidth: 1,
    borderTopColor: "#ebebeb",
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  clearItemText: {
    fontSize: 12,
    color: "#CC3B0F",
    fontWeight: "600",
  },
});

/* ── Main component ──────────────────────────────────────── */
const MyTicketsTable: React.FC<Props> = ({
  query,
  totalCount,
  filteredTickets,
  onQueryChange,
  onRowPress,
  sortColumn,
  sortDirection,
  onSort,
  webFilters,
  onWebFilterChange,
  filterOptions,
  mobile,
  filterCount = 0,
  onFiltersPress,
  onNewTicket,
  onClearFilters,
  error,
  loading,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  scrollToTicketId,
}) => {
  const isDark = useTheme().resolvedTheme === "dark";
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  // ── Scroll-to-ticket restoration ─────────────────────────────────────────
  // rowRefs maps ticketId → the rendered row View ref (native) or DOM nativeID (web).
  const rowRefs = useRef<Record<string, View | null>>({});
  // outerScrollRef is used on native to scroll the enclosing vertical ScrollView.
  // On web the table rows use a horizontal ScrollView; vertical scrolling is done
  // by the parent ScrollView in the screen, so we use DOM scrollIntoView instead.
  const outerScrollRef = useRef<ScrollView>(null);

  // Effect: when scrollToTicketId changes and tickets are present, scroll to the row.
  useEffect(() => {
    if (!scrollToTicketId || filteredTickets.length === 0) return;

    // Small delay to ensure the list has fully laid out before measuring.
    const timer = setTimeout(() => {
      if (Platform.OS === "web") {
        // Web: find the rendered DOM element by nativeID and call scrollIntoView.
        try {
          const el = (globalThis as any).document?.getElementById(
            `my-ticket-row-${scrollToTicketId}`
          );
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        } catch {
          // Graceful no-op if DOM APIs are unavailable.
        }
      } else {
        // Native (iOS iPad): measure the row View relative to the outer scroll container
        // and call scrollTo so the row is centred in the viewport.
        const rowRef = rowRefs.current[scrollToTicketId];
        const scrollView = outerScrollRef.current as any;
        if (rowRef && scrollView) {
          rowRef.measureLayout(
            scrollView,
            (_x: number, y: number, _w: number, h: number) => {
              // Scroll so the row is vertically centred.
              scrollView.scrollTo({ y: Math.max(0, y - h), animated: true });
            },
            () => { /* measureLayout failed gracefully */ }
          );
        }
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [scrollToTicketId, filteredTickets]);

  // ── Dual (top + bottom) horizontal scrollbar sync ─────────────────────────
  const tableScrollRef = useRef<ScrollView>(null);
  const topScrollBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    const top = topScrollBarRef.current;
    const bottom = (tableScrollRef.current as any)?.getScrollableNode?.() as HTMLElement | null;
    if (!top || !bottom) return;
    let syncing = false;
    const fromTop = () => { if (syncing) return; syncing = true; bottom.scrollLeft = top.scrollLeft; syncing = false; };
    const fromBottom = () => { if (syncing) return; syncing = true; top.scrollLeft = bottom.scrollLeft; syncing = false; };
    top.addEventListener("scroll", fromTop, { passive: true });
    bottom.addEventListener("scroll", fromBottom, { passive: true });
    return () => {
      top.removeEventListener("scroll", fromTop);
      bottom.removeEventListener("scroll", fromBottom);
    };
  }, []);

  function toggleFilter(key: keyof WebFilters, value: string) {
    const current = webFilters[key];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onWebFilterChange({ ...webFilters, [key]: next });
  }

  function clearFilter(key: keyof WebFilters) {
    onWebFilterChange({ ...webFilters, [key]: [] });
  }

  const hasActiveFilters = Object.values(webFilters).some((v) => v.length > 0);
  const hasActiveSearch = query.trim().length > 0;

  /**
   * Determines which empty/error state to render in place of the table body.
   * Returns null when the table should render rows normally.
   */
  function resolveEmptyState(containerStyle: object) {
    // 1. Service error
    if (error) {
      return (
        <View style={[emStyles.container, containerStyle]}>
          <MaterialIcons name="cloud-off" size={40} color="#cc3b0f" />
          <Text style={emStyles.heading}>Unable to load your tickets</Text>
          <Text style={emStyles.body}>Please refresh the page or try again later.</Text>
        </View>
      );
    }
    // 2. Loading — show inline spinner inside the table body
    if (loading) {
      return (
        <View style={[emStyles.container, containerStyle]}>
          <ActivityIndicator size="large" color="#034895" />
          <Text style={emStyles.body}>Loading tickets…</Text>
        </View>
      );
    }
    // 3. No tickets at all (user has never submitted)
    if (totalCount === 0) {
      return (
        <View style={[emStyles.container, containerStyle]}>
          <MaterialIcons name="inbox" size={40} color="#c0c8d4" />
          <Text style={emStyles.heading}>No tickets have been submitted yet.</Text>
          <Text style={emStyles.body}>When you submit a ticket it will appear here.</Text>
          {onNewTicket && (
            <Pressable style={emStyles.btn} onPress={onNewTicket}>
              <MaterialIcons name="add" size={16} color="#ffffff" />
              <Text style={emStyles.btnText}>Submit New Ticket</Text>
            </Pressable>
          )}
        </View>
      );
    }
    // 4. Filters / search returned no results
    if (filteredTickets.length === 0) {
      return (
        <View style={[emStyles.container, containerStyle]}>
          <MaterialIcons name="search-off" size={40} color="#c0c8d4" />
          <Text style={emStyles.heading}>No tickets match the selected filters</Text>
          <Text style={emStyles.body}>Try adjusting your search or filter criteria.</Text>
          {(hasActiveFilters || hasActiveSearch) && onClearFilters && (
            <Pressable style={emStyles.btnOutline} onPress={onClearFilters}>
              <Text style={emStyles.btnOutlineText}>Clear Filters</Text>
            </Pressable>
          )}
        </View>
      );
    }
    return null;
  }

  /* ── Mobile card layout ──────────────────────────────────── */
  if (mobile) {
    return (
      <View>
        {/* Search + Filters row */}
        <View style={mobileStyles.toolbarRow}>
          <View style={mobileStyles.searchWrap}>
            <MaterialIcons name="search" size={18} color="#818181" style={mobileStyles.searchIcon} />
            <TextInput
              style={mobileStyles.searchInput}
              value={query}
              onChangeText={onQueryChange}
              placeholder="Search my tickets"
              placeholderTextColor="#9099aa"
            />
          </View>
          <Pressable style={mobileStyles.filterBtn} onPress={onFiltersPress}>
            <MaterialIcons name="filter-list" size={18} color="#3a3a3a" />
            <Text style={mobileStyles.filterBtnText}>Filters</Text>
            {filterCount > 0 ? (
              <View style={mobileStyles.filterBadge}>
                <Text style={mobileStyles.filterBadgeText}>{filterCount}</Text>
              </View>
            ) : null}
          </Pressable>
        </View>

        {/* Card list */}
        <View style={mobileStyles.cardList}>
          {resolveEmptyState({ marginHorizontal: 16, marginTop: 32 }) ??
            filteredTickets.map((ticket) => {
            const statusLabel = STATUS_LABEL[ticket.status] ?? "New";
            const urgencyLabel = formatUrgencyLabel(ticket.urgency);
            const urgencyBadgeStyles = getMobileUrgencyBadgeStyles(ticket.urgency);
            const statusKey = `status${statusLabel.replace(/[\s()]+/g, "")}` as keyof typeof mobileStyles;
            const statusDotKey = `status${statusLabel.replace(/[\s()]+/g, "") }Dot` as keyof typeof mobileStyles;
            const statusTextKey = `status${statusLabel.replace(/[\s()]+/g, "") }Text` as keyof typeof mobileStyles;
            const borderColor = STATUS_BORDER_COLOR[ticket.status] || "#d0d0d0";
            const isWaitingUser = ticket.status === "waiting-user";

            return (
              <Pressable
                key={ticket.id}
                style={({ pressed }) => [mobileStyles.card, pressed ? { opacity: 0.85 } : null]}
                onPress={() => onRowPress?.(ticket.id)}
              >
                {/* Colored left accent border */}
                <View style={[mobileStyles.cardLeftBorder, { backgroundColor: borderColor }]} />
                <View style={mobileStyles.cardContent}>
                  <Text style={mobileStyles.ticketId}>{ticket.id}</Text>
                  <Text style={mobileStyles.ticketTitle}>{truncate(ticket.title, 60)}</Text>

                  <View style={mobileStyles.badgeRow}>
                    <View style={[mobileStyles.badge, mobileStyles[statusKey] as any]}>
                      <View style={[mobileStyles.badgeDot, mobileStyles[statusDotKey] as any]} />
                      <Text style={[mobileStyles.badgeText, mobileStyles[statusTextKey] as any]}>
                        {statusLabel}
                      </Text>
                    </View>
                    {ticket.urgency ? (
                      <View style={[mobileStyles.badge, urgencyBadgeStyles.badgeStyle]}>
                        <View style={[mobileStyles.badgeDot, urgencyBadgeStyles.dotStyle]} />
                        <Text style={[mobileStyles.badgeText, urgencyBadgeStyles.textStyle]}>
                          {urgencyLabel}
                        </Text>
                      </View>
                    ) : (
                      <View style={[mobileStyles.badge, { backgroundColor: "#F3F4F6" }]}>
                        <Text style={[mobileStyles.badgeText, { color: "#6B7280" }]}>
                          {urgencyLabel}
                        </Text>
                      </View>
                    )}
                  </View>

                  {ticket.hasDeclinedUnit ? (
                    <View style={DECLINED_PILL_STYLE}>
                      <Text style={DECLINED_PILL_TEXT_STYLE}>Declined, negligent damage</Text>
                    </View>
                  ) : null}

                  <View style={mobileStyles.metaRow}>
                    <View style={mobileStyles.metaItem}>
                      <MaterialIcons name="place" size={13} color="#818181" />
                      <Text style={mobileStyles.metaText}>{formatAccessRequestLocation(ticket)}</Text>
                    </View>
                    <View style={mobileStyles.metaItem}>
                      <MaterialIcons name="calendar-today" size={13} color="#818181" />
                      <Text style={mobileStyles.metaText}>{formatDateLocal(ticket.submitted)}</Text>
                    </View>
                  </View>

                  {isWaitingUser ? (
                    <View style={mobileStyles.actionNeededRow}>
                      <MaterialIcons name="error-outline" size={14} color="#CC6E16" />
                      <Text style={mobileStyles.actionNeededText}>
                        Action needed: Sustain Team is waiting for your response
                      </Text>
                    </View>
                  ) : null}
                  {ticket.status === "closed-merged" && ticket.mergedIntoTicketId ? (
                    <Pressable
                      onPress={(e) => { e.stopPropagation?.(); onRowPress?.(ticket.mergedIntoTicketId!); }}
                      style={mobileStyles.mergedIntoRow}
                    >
                      <MaterialIcons name="call-merge" size={13} color="#7871E2" />
                      <Text style={mobileStyles.mergedIntoLink}>View primary: {ticket.mergedIntoTicketId}</Text>
                    </Pressable>
                  ) : null}
                </View>
              </Pressable>
            );
          })}
        </View>

        {/* Mobile pagination — only shown when there are tickets */}
        {!loading && !error && totalCount > 0 && (
          <>
            <Text style={mobileStyles.mobilePaginationCountText}>
              Showing {(currentPage - 1) * 100 + 1}–{Math.min(currentPage * 100, totalCount)} of {totalCount} tickets
            </Text>
            <View style={mobileStyles.mobilePaginationContainer}>
              <View style={mobileStyles.mobilePaginationBox}>
                {/* Left arrow */}
                <Pressable
                  disabled={currentPage <= 1}
                  onPress={() => currentPage > 1 && onPageChange?.(currentPage - 1)}
                  style={[
                    mobileStyles.mobilePaginationCell,
                    mobileStyles.mobilePaginationArrowCell,
                    currentPage <= 1 && mobileStyles.mobilePaginationCellDisabled,
                  ]}
                  accessibilityLabel="Previous page"
                >
                  <MaterialIcons name="chevron-left" size={20} color={currentPage <= 1 ? "#A0A0A0" : "#005CBC"} />
                </Pressable>

                {/* Current page — blue highlighted */}
                <View style={[mobileStyles.mobilePaginationCell, mobileStyles.mobilePaginationCurrentCell]}>
                  <Text style={mobileStyles.mobilePaginationCurrentText}>{currentPage}</Text>
                </View>

                {/* Next page number — tappable */}
                <Pressable
                  disabled={currentPage >= totalPages}
                  onPress={() => currentPage < totalPages && onPageChange?.(currentPage + 1)}
                  style={[
                    mobileStyles.mobilePaginationCell,
                    mobileStyles.mobilePaginationNextCell,
                    currentPage >= totalPages && mobileStyles.mobilePaginationCellDisabled,
                  ]}
                  accessibilityLabel="Next page number"
                >
                  <Text style={mobileStyles.mobilePaginationNextText}>
                    {currentPage < totalPages ? currentPage + 1 : currentPage}
                  </Text>
                </Pressable>

                {/* Right arrow */}
                <Pressable
                  disabled={currentPage >= totalPages}
                  onPress={() => currentPage < totalPages && onPageChange?.(currentPage + 1)}
                  style={[
                    mobileStyles.mobilePaginationCell,
                    mobileStyles.mobilePaginationArrowCell,
                    currentPage >= totalPages && mobileStyles.mobilePaginationCellDisabled,
                  ]}
                  accessibilityLabel="Next page"
                >
                  <MaterialIcons name="chevron-right" size={20} color={currentPage >= totalPages ? "#A0A0A0" : "#005CBC"} />
                </Pressable>
              </View>
            </View>
          </>
        )}
      </View>
    );
  }

  /* ── Web / iPad table layout ─────────────────────────────── */
  return (
    <View style={styles.dataTableWrap}>
      {/* Toolbar */}
      <View style={styles.tableToolbar}>
        <View style={styles.searchWrap}>
          <MaterialIcons name="search" size={14} color="#818181" style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            value={query}
            onChangeText={onQueryChange}
            placeholder="Search by Ticket ID or Title"
            placeholderTextColor="#9099aa"
          />
        </View>
        <Text style={styles.resultCount}>{filteredTickets.length} of {totalCount} tickets</Text>
        {hasActiveFilters && (
          <Pressable
            style={styles.clearFiltersBtn}
            onPress={() =>
              onWebFilterChange({ urgency: [], status: [], category: [], subcategory: [], location: [], title: [] })
            }
          >
            <Text style={styles.clearFiltersBtnText}>Clear all filters</Text>
          </Pressable>
        )}
      </View>

      {Platform.OS === "web" && React.createElement("div", {
        ref: topScrollBarRef,
        style: { overflowX: "scroll", overflowY: "hidden", height: 14 },
      }, React.createElement("div", { style: { width: 1540, height: 1 } }))}

      <ScrollView ref={tableScrollRef} horizontal showsHorizontalScrollIndicator>
        <View style={styles.tableMinWidth}>
          {/* Header row — sort arrows on all columns; filter icon on filterable columns */}
          <View style={styles.tableHeadRow}>
            <SortHeader label="Ticket ID"           col="id"          current={sortColumn} direction={sortDirection} onSort={onSort} style={styles.colId} />
            <SortFilterHeader label="Title"         col="title"       current={sortColumn} direction={sortDirection} onSort={onSort} filterKey="title" filterOptions={filterOptions.titles} selected={webFilters.title} onToggle={toggleFilter} onClear={clearFilter} style={styles.colTitle} />
            <SortFilterHeader label="Category"      col="category"    current={sortColumn} direction={sortDirection} onSort={onSort} filterKey="category" filterOptions={filterOptions.categories} selected={webFilters.category} onToggle={toggleFilter} onClear={clearFilter} style={styles.colCategory} />
            <SortFilterHeader label="Sub-Category"  col="subcategory" current={sortColumn} direction={sortDirection} onSort={onSort} filterKey="subcategory" filterOptions={filterOptions.subcategories} selected={webFilters.subcategory} onToggle={toggleFilter} onClear={clearFilter} style={styles.colSubCategory} />
            <SortFilterHeader label="Location / Market / Region" col="location" current={sortColumn} direction={sortDirection} onSort={onSort} filterKey="location" filterOptions={filterOptions.locations} selected={webFilters.location} onToggle={toggleFilter} onClear={clearFilter} style={styles.colLocation} />
            <SortFilterHeader label="Urgency"       col="urgency"     current={sortColumn} direction={sortDirection} onSort={onSort} filterKey="urgency" filterOptions={filterOptions.urgencies} filterLabelMap={URGENCY_LABEL} selected={webFilters.urgency} onToggle={toggleFilter} onClear={clearFilter} style={styles.colUrgency} />
            <SortFilterHeader label="Status"        col="status"      current={sortColumn} direction={sortDirection} onSort={onSort} filterKey="status" filterOptions={filterOptions.statuses} filterLabelMap={STATUS_LABEL} selected={webFilters.status} onToggle={toggleFilter} onClear={clearFilter} style={styles.colStatus} />
            <SortHeader label="Submitted"           col="submitted"   current={sortColumn} direction={sortDirection} onSort={onSort} style={styles.colDate} />
            <SortHeader label="Last Updated"        col="lastUpdated" current={sortColumn} direction={sortDirection} onSort={onSort} style={styles.colDate} />
          </View>

          {/* Body rows — or empty / error state */}
          {resolveEmptyState({ paddingVertical: 56 }) ?? filteredTickets.map((ticket) => {
            const urgencyLabel = formatUrgencyLabel(ticket.urgency);
            const statusLabel = STATUS_LABEL[ticket.status] ?? "New";
            const urgencyBadgeStyles = getDesktopUrgencyBadgeStyles(ticket.urgency);
            const statusStyleKey = `status${statusLabel.replace(/[\s()]+/g, "")}` as keyof typeof styles;
            const statusDotKey = `status${statusLabel.replace(/[\s()]+/g, "") }Dot` as keyof typeof styles;
            const statusTextKey = `status${statusLabel.replace(/[\s()]+/g, "") }Text` as keyof typeof styles;
            const waitingUser = ticket.status === "waiting-user";
            const hovered = hoveredRowId === ticket.id;
            const rowNativeId = `my-ticket-row-${ticket.id}`;

            return (
              <Pressable
                key={ticket.id}
                // nativeID / id prop: used by scrollIntoView (web) and measureLayout (native)
                // @ts-ignore — id is valid on React Native Web; nativeID on native
                id={rowNativeId}
                nativeID={rowNativeId}
                ref={(el) => { rowRefs.current[ticket.id] = el as View | null; }}
                // @ts-ignore — onMouseEnter/onMouseLeave are valid on React Native Web
                onMouseEnter={() => setHoveredRowId(ticket.id)}
                onMouseLeave={() => setHoveredRowId(null)}
                style={({ pressed }) => [
                  styles.tableBodyRow,
                  waitingUser ? styles.tableBodyRowWaitingUser : null,
                  hovered && !waitingUser ? styles.tableBodyRowHovered : null,
                  hovered && waitingUser ? styles.tableBodyRowWaitingUserHovered : null,
                  pressed ? styles.tableBodyRowPressed : null,
                  // The waiting-user gradients are CSS background-image values, which the
                  // Dark stylesheet sweep does not rewrite; override them explicitly.
                  isDark && waitingUser
                    ? ({
                        backgroundColor: hovered ? "#3D2608" : "#2A1E10",
                        backgroundImage: hovered
                          ? "linear-gradient(90deg, #4A2E0C 0%, #2B2B2B 60%)"
                          : "linear-gradient(90deg, #3D2608 0%, transparent 40%)",
                      } as any)
                    : null,
                ]}
                onPress={() => onRowPress?.(ticket.id)}
              >
                {/* Ticket ID */}
                <Text style={[styles.tdCell, styles.colId, styles.tdId]}>{ticket.id}</Text>

                {/* Title — truncated at 60 chars */}
                <Text
                  style={[styles.tdCell, styles.colTitle, styles.tdTitle]}
                  numberOfLines={2}
                >
                  {truncate(ticket.title, 60)}
                </Text>

                {/* Category */}
                <Text style={[styles.tdCell, styles.colCategory]}>{ticket.category}</Text>

                {/* Sub-Category */}
                <Text style={[styles.tdCell, styles.colSubCategory, styles.tdSubCategory]}>
                  {ticket.subcategory ?? "—"}
                </Text>

                {/* Location / Market / Region */}
                <View style={[styles.tdCellWrap, styles.colLocation]}>
                  <Text style={styles.tdCell}>{formatAccessRequestLocation(ticket)}</Text>
                  {ticket.ticketLevel ? (
                    <View style={styles.levelPill}>
                      <Text style={styles.levelPillText}>{ticket.ticketLevel}</Text>
                    </View>
                  ) : ticket.sapId ? (
                    <Text style={styles.sapIdText}>SAP: {ticket.sapId}</Text>
                  ) : null}
                </View>

                {/* Urgency */}
                <View style={[styles.tdCellWrap, styles.colUrgency]}>
                  {ticket.urgency ? (
                    <View style={[styles.badge, urgencyBadgeStyles.badgeStyle]}>
                      <View style={[styles.badgeDot, urgencyBadgeStyles.dotStyle]} />
                      <Text style={[styles.badgeText, urgencyBadgeStyles.textStyle]}>{urgencyLabel}</Text>
                    </View>
                  ) : (
                    <View style={[styles.badge, { backgroundColor: "#F3F4F6" }]}>
                      <Text style={[styles.badgeText, { color: "#6B7280" }]}>{urgencyLabel}</Text>
                    </View>
                  )}
                </View>

                {/* Status */}
                <View style={[styles.tdCellWrap, styles.colStatus]}>
                  <View
                    style={[
                      styles.badge,
                      styles[statusStyleKey] as any,
                      isDark && STATUS_BG_DARK[ticket.status] ? { backgroundColor: STATUS_BG_DARK[ticket.status] } : null,
                    ]}
                  >
                    <View
                      style={[
                        styles.badgeDot,
                        styles[statusDotKey] as any,
                        isDark && STATUS_COLOR_DARK[ticket.status] ? { backgroundColor: STATUS_COLOR_DARK[ticket.status] } : null,
                      ]}
                    />
                    <Text
                      style={[
                        styles.badgeText,
                        styles[statusTextKey] as any,
                        isDark && STATUS_COLOR_DARK[ticket.status] ? { color: STATUS_COLOR_DARK[ticket.status] } : null,
                      ]}
                      {...(isDark ? ({ dataSet: { lhKeepColor: "1" } } as any) : {})}
                    >
                      {statusLabel}
                    </Text>
                  </View>
                  {waitingUser && (
                    <View style={[styles.actionNeededBadge, isDark && { backgroundColor: "#3D2608" }]}>
                      <MaterialIcons name="error-outline" size={11} color={isDark ? "#FFAD5C" : "#A85100"} />
                      <Text
                        style={[styles.actionNeededText, isDark && { color: "#FFAD5C" }]}
                        {...(isDark ? ({ dataSet: { lhKeepColor: "1" } } as any) : {})}
                      >
                        Action needed
                      </Text>
                    </View>
                  )}
                  {ticket.hasDeclinedUnit ? (
                    <View style={DECLINED_PILL_STYLE}>
                      <Text style={DECLINED_PILL_TEXT_STYLE}>Declined, negligent damage</Text>
                    </View>
                  ) : null}
                  {ticket.status === "closed-merged" && ticket.mergedIntoTicketId ? (
                    <Pressable
                      onPress={(e) => { e.stopPropagation?.(); onRowPress?.(ticket.mergedIntoTicketId!); }}
                      style={styles.mergedIntoRow}
                    >
                      <MaterialIcons name="call-merge" size={11} color="#7871E2" />
                      <Text style={styles.mergedIntoLink}>Primary: {ticket.mergedIntoTicketId}</Text>
                    </Pressable>
                  ) : null}
                </View>

                {/* Submitted */}
                <Text style={[styles.tdCell, styles.colDate, styles.tdDate]}>
                  {formatDateLocal(ticket.submitted)}
                </Text>

                {/* Last Updated */}
                <Text style={[styles.tdCell, styles.colDate, styles.tdDate]}>
                  {formatDateLocal(ticket.lastUpdated)}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </ScrollView>

      {!error && !loading && filteredTickets.length > 0 && (
        <View style={styles.pagination}>
          <Text style={styles.paginationText}>
            Showing {(currentPage - 1) * 100 + 1}–{Math.min(currentPage * 100, totalCount)} of {totalCount} tickets
          </Text>
          <View style={styles.paginationGroup}>
            <Pressable
              disabled={currentPage <= 1}
              style={currentPage <= 1 ? styles.pgButtonDisabled : styles.pgButton}
              onPress={() => currentPage > 1 && onPageChange?.(currentPage - 1)}
            >
              <MaterialIcons name="chevron-left" size={14} color={currentPage <= 1 ? "#a8a8a8" : "#034895"} />
            </Pressable>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = i + 1;
              const isActive = page === currentPage;
              return (
                <Pressable
                  key={page}
                  style={isActive ? styles.pgButtonActive : styles.pgButton}
                  onPress={() => !isActive && onPageChange?.(page)}
                >
                  <Text style={isActive ? styles.pgButtonActiveText : styles.pgButtonText}>{page}</Text>
                </Pressable>
              );
            })}
            <Pressable
              disabled={currentPage >= totalPages}
              style={currentPage >= totalPages ? styles.pgButtonDisabled : styles.pgButton}
              onPress={() => currentPage < totalPages && onPageChange?.(currentPage + 1)}
            >
              <MaterialIcons name="chevron-right" size={14} color={currentPage >= totalPages ? "#a8a8a8" : "#034895"} />
            </Pressable>
          </View>
        </View>
      )}
    </View>
  );
};

export default MyTicketsTable;

/* ── Empty / error state styles ─────────────────────────────── */
const emStyles = StyleSheet.create({
  container: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 24,
    gap: 10,
  },
  heading: {
    fontSize: 16,
    fontWeight: "700",
    color: "#2d2d2d",
    textAlign: "center",
    marginTop: 6,
  },
  body: {
    fontSize: 13,
    color: "#818181",
    textAlign: "center",
    lineHeight: 19,
  },
  btn: {
    marginTop: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#034895",
    borderRadius: 100,
    paddingHorizontal: 20,
    paddingVertical: 10,
    ...Platform.select({ web: { cursor: "pointer" } as any }),
  },
  btnText: {
    fontSize: 13,
    fontWeight: "700",
    color: "#ffffff",
  },
  btnOutline: {
    marginTop: 8,
    borderWidth: 1.5,
    borderColor: "#034895",
    borderRadius: 100,
    paddingHorizontal: 20,
    paddingVertical: 10,
    ...Platform.select({ web: { cursor: "pointer" } as any }),
  },
  btnOutlineText: {
    fontSize: 13,
    fontWeight: "700",
    color: "#034895",
  },
});
